import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import { useDispatch } from "redux-react-hook";

import { Text } from "src/library/components/Text/Text";
import { useCurrentUser } from "src/core/users/hooks";
import { INotification } from "src/core/notifications/api/notifications.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import { useNotificationsApi } from "../api/notifications.api";
import Notification from "./Notification";
import { toggleNotifications } from "../store/notifications.actions";

const NotificationDashboardWidget: React.ReactFCWithChildren = () => {
  const [ { currentUser } ] = useCurrentUser();
  const dispatch = useDispatch();
  const [ listNotificationsResponse, listNotifications ] = useNotificationsApi("GET_USER_NOTIFICATIONS");
  const [ notifications, setNotifications ] = React.useState<INotification[]>([]);
  const userId = React.useMemo(() => currentUser?.id, [ currentUser ]);

  const getNotifications = React.useCallback(() => {
    if (userId) {
      listNotifications({
        pagination: {
          limit: 10,
          offset: 0,
          page: 0,
          sort: [ [ "createdAt", "DESC" ] ]
        },
        filters: {
          notificationUsers: {
            dismissedAt: { $eq: null },
            userId
          }
        },
        userId
      }, {
        cache: {
          maxAge: cacheMinutesToMilliseconds(5),
          clearOnError: true
        }
      });
    }
  }, [ userId, listNotifications ]);

  React.useEffect(() => {
    getNotifications();
  }, [ getNotifications ]);

  React.useEffect(() => {
    if (listNotificationsResponse.data) {
      setNotifications(currentNotifications => {
        return _.uniqBy([ ...currentNotifications, ...(listNotificationsResponse.data?.notifications.result || []) ], "id");
      });
    }
  }, [ listNotificationsResponse ]);

  const handleOpenNotification = (notificationId: number) => {
    dispatch(toggleNotifications(true, notificationId));
  };

  let count = 0;

  const unreadNotifications = React.useMemo(() => {
    return _.filter(notifications, notification => {
      if (
        notification.notificationUsers &&
      notification.notificationUsers[ 0 ] &&
      notification.notificationUsers[ 0 ].dismissedAt === null &&
      count < 5
      ) {
        count++;

        return true;
      } else {
        return false;
      }
    });
  }, [ count, notifications ]);

  return (
    <NotificationDashboardWidgetWrapper>
      <div className="list">
        {!unreadNotifications.length && (
          <div>
            <Text>
              No new notifications
            </Text>
          </div>
        )}

        {listNotificationsResponse.pending && (
          <LoadingSpinner
            type="circular-pulse"
            size={30}
          />
        )}

        {_.map(
          unreadNotifications,
          n => (
            <Notification
              dismissedNotificationCallback={notificationRead => {
                setNotifications(currentNotifications =>
                  _.map(currentNotifications, notification =>
                    notificationRead.notificationId === notification.id ?
                      {
                        ...notification,
                        notificationUsers: [ notificationRead ]
                      } :
                      notification
                  )
                );
              }}
              notification={n}
              viewNotification={handleOpenNotification}
            />
          )
        )}
      </div>
    </NotificationDashboardWidgetWrapper>
  );
};

const NotificationDashboardWidgetWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.md};
    position: relative;

    .list {
      overflow-y: scroll;
      height: 100%;
    }
  `}
`;

export default NotificationDashboardWidget;
